import { useEffect, useState } from 'react'
import { Button, ConfirmActionModal, TextInput, MemberControlsEditor } from '../../Common'
import {
  deleteControlProfile,
  subscribeToControlProfiles,
  upsertControlProfile,
} from '../../../services/memberControlsService'
import type { ControlProfile, MemberControls, MemberControlsSettings } from '../../../types/memberControls'
import { useNls } from '../../../nls/nlsProvider'
import './ControlProfilesManager.css'

export interface ControlProfilesManagerProps {
  workspaceId: string
  members: Array<{ id: string; controls?: MemberControlsSettings | null }>
  isOpen: boolean
  onClose: () => void
  canManage: boolean
}

export const ControlProfilesManager = ({
  workspaceId,
  members,
  isOpen,
  onClose,
  canManage,
}: ControlProfilesManagerProps) => {
  const { t } = useNls()
  const [profiles, setProfiles] = useState<ControlProfile[]>([])
  const [isEditing, setIsEditing] = useState(false)
  const [editingProfile, setEditingProfile] = useState<ControlProfile | null>(null)
  const [profileName, setProfileName] = useState('')
  const [draftControls, setDraftControls] = useState<MemberControls>({})
  const [pendingDelete, setPendingDelete] = useState<ControlProfile | null>(null)
  const [isBusy, setIsBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen || !workspaceId) return undefined
    return subscribeToControlProfiles(workspaceId, (fetched) => {
      setProfiles(fetched)
    })
  }, [isOpen, workspaceId])

  if (!isOpen || !canManage) return null

  const handleStartCreate = () => {
    setEditingProfile(null)
    setProfileName('')
    setDraftControls({})
    setIsEditing(true)
    setError(null)
  }

  const handleStartEdit = (profile: ControlProfile) => {
    setEditingProfile(profile)
    setProfileName(profile.name)
    setDraftControls(profile.controls ?? {})
    setIsEditing(true)
    setError(null)
  }

  const handleSave = async () => {
    if (!profileName.trim()) return
    setIsBusy(true)
    setError(null)
    try {
      await upsertControlProfile({
        workspaceId,
        profileId: editingProfile?.id,
        name: profileName.trim(),
        controls: draftControls,
      })
      setIsEditing(false)
      setEditingProfile(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save profile')
    } finally {
      setIsBusy(false)
    }
  }

  const handleConfirmDelete = async () => {
    if (!pendingDelete?.id) return
    setIsBusy(true)
    setError(null)
    try {
      await deleteControlProfile({
        workspaceId,
        profileId: pendingDelete.id,
      })
      setPendingDelete(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete profile')
    } finally {
      setIsBusy(false)
    }
  }

  const getUsageCount = (profileId?: string) => {
    if (!profileId) return 0
    return members.filter((m) => m.controls?.profileId === profileId).length
  }

  return (
    <div className="controlProfilesModalOverlay" data-testid="control-profiles-modal">
      <div
        className="controlProfilesModalContent"
        aria-modal="true"
        aria-label={t('NLSControlsProfilesTitle')}
      >
        <header className="controlProfilesModalHeader">
          <h2>{t('NLSControlsProfilesTitle')}</h2>
          <div className="controlProfilesModalHeaderActions">
            {!isEditing && (
              <Button
                variant="primary"
                size="sm"
                onClick={handleStartCreate}
                data-testid="create-profile-btn"
              >
                {t('NLSControlsCreateProfile')}
              </Button>
            )}
            <Button
              variant="tertiary"
              size="sm"
              onClick={onClose}
              data-testid="close-profiles-modal-btn"
            >
              Close
            </Button>
          </div>
        </header>

        {error ? <div className="controlProfilesError" role="alert">{error}</div> : null}

        {isEditing ? (
          <div className="controlProfileEditor" data-testid="control-profile-editor">
            <h3>{editingProfile ? t('NLSControlsEditProfile') : t('NLSControlsCreateProfile')}</h3>
            <label className="controlProfileField">
              <span>{t('NLSControlsProfileName')}</span>
              <TextInput
                value={profileName}
                onChange={(val) => setProfileName(val)}
                placeholder="Profile name"
                aria-label={t('NLSControlsProfileName')}
              />
            </label>

            <MemberControlsEditor
              isProfileMode={true}
              controls={draftControls}
              onChange={(patch) => setDraftControls(patch)}
              showAi={true}
            />

            <div className="controlProfileEditorActions">
              <Button
                variant="primary"
                size="sm"
                disabled={!profileName.trim() || isBusy}
                onClick={handleSave}
                data-testid="save-profile-btn"
              >
                {isBusy ? 'Saving...' : 'Save'}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={isBusy}
                onClick={() => {
                  setIsEditing(false)
                  setEditingProfile(null)
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="controlProfilesListWrapper">
            {profiles.length === 0 ? (
              <div className="controlProfilesEmpty" data-testid="control-profiles-empty">
                {t('NLSControlsProfilesEmpty')}
              </div>
            ) : (
              <ul className="controlProfilesList" data-testid="control-profiles-list">
                {profiles.map((p) => {
                  const count = getUsageCount(p.id)
                  return (
                    <li key={p.id} className="controlProfileRow" data-testid={`control-profile-row-${p.id}`}>
                      <div className="controlProfileRowInfo">
                        <strong className="controlProfileRowName">{p.name}</strong>
                        <span className="controlProfileRowCount">
                          {t('NLSControlsUsedByCount').replace('{count}', String(count))}
                        </span>
                      </div>
                      <div className="controlProfileRowActions">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleStartEdit(p)}
                          data-testid={`edit-profile-${p.id}`}
                        >
                          {t('NLSControlsEditProfile')}
                        </Button>
                        <Button
                          variant="tertiary"
                          tone="danger"
                          size="sm"
                          onClick={() => setPendingDelete(p)}
                          data-testid={`delete-profile-${p.id}`}
                        >
                          {t('NLSControlsProfileDelete')}
                        </Button>
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        )}

        {pendingDelete ? (
          <ConfirmActionModal
            open={Boolean(pendingDelete)}
            title={t('NLSControlsDeleteConfirmTitle')}
            body={t('NLSControlsDeleteConfirmBody').replace(
              '{count}',
              String(getUsageCount(pendingDelete.id)),
            )}
            confirmLabel="Delete profile"
            danger
            onClose={() => setPendingDelete(null)}
            onConfirm={handleConfirmDelete}
          />
        ) : null}
      </div>
    </div>
  )
}
